import { useConfirm } from "../confirm/ConfirmProvider";
import { toast } from "../toast/toast";
import React, { useEffect, useState } from "react";
import { User } from "../../../backend/src/types";
import { useUser } from "../contexts/UserContext";
import { deleteUser, getUserList } from "../fetch/UserRequest";
import styles from "../styles/modules/AdminPage.module.css";
import "../styles/sanitize.css";
import UserPopup from "./UserPopup";
import ActionIconButton from "./ActionIconButton";

interface UserListProps {
  onLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const UsersList: React.FC<UserListProps> = ({ onLoading }) => {
  const { user } = useUser();
  const confirm = useConfirm();

  const [userList, setUserList] = useState<User[]>([]);
  const [isShowPopup, setIsShowPopup] = useState<boolean>(false);
  const [isCSVMode, setIsCSVMode] = useState<boolean>(false);
  const [targetUser, setTargetUser] = useState<User | null>(null);

  useEffect(() => {
    if (user) loadUserList(user);
  }, [user]);

  async function loadUserList(user: User) {
    onLoading(true);
    const users = await getUserList({ user });
    setUserList(users);
    onLoading(false);
  }

  // ユーザー削除は物理削除。履歴の「誰が」はサーバー側で名前スナップショット
  // されるため、削除後も試薬の履歴タブ・CSVには名前が残る。取り消しは不可。
  async function handleDeleteUser(target: User) {
    if (target.id === user?.id) {
      toast.error("自身を削除することはできません。");
      return;
    }
    if (
      !(await confirm(
        `「${target.name}」を削除します。\nこの操作は取り消せません。よろしいですか？`,
        { danger: true, confirmText: "削除" },
      ))
    ) {
      return;
    }

    onLoading(true);
    await deleteUser({
      target_user_id: target.id,
      tenant_id: user?.tenant_id ?? 0,
    });
    setUserList((prev) => prev.filter((u) => u.id !== target.id));
    onLoading(false);
  }

  function pushPopupButton(targetUser: User | null, isMode: boolean) {
    setTargetUser(targetUser);
    setIsCSVMode(isMode);
    setIsShowPopup(true);
  }

  function updateUserList(updatedUser: User) {
    setUserList((prevList) => {
      const map = new Map(prevList.map((g) => [g.id, g]));
      map.set(updatedUser.id, updatedUser);

      const sorted = Array.from(map.values()).sort((a, b) =>
        a.name.localeCompare(b.name, "ja"),
      );

      return sorted;
    });
  }

  return (
    <>
      <div className={styles["list-section"]}>
        <div className={styles["popup-button"]}>
          <h3>ユーザー設定</h3>
          <div className={styles["user-button"]}>
            <button
              className={styles["csv-button"]}
              onClick={() => pushPopupButton(null, true)}
            >
              CSVのインポート
            </button>
            <button
              className={styles["add-circle"]}
              onClick={() => pushPopupButton(null, false)}
              title="ユーザー追加"
              aria-label="ユーザー追加"
            >
              +
            </button>
          </div>
        </div>
        <div>
          <table>
            <thead>
              <tr>
                <th>名前</th>
                <th>学籍番号</th>
                <th>最終ログイン</th>
                <th>編集</th>
                <th>削除</th>
              </tr>
            </thead>
            <tbody>
              {userList.length > 0 ? (
                userList.map((targetUser) => (
                  <tr key={targetUser.id}>
                    <td>{targetUser.name}</td>
                    <td>{targetUser.student_no}</td>
                    <td>
                      {targetUser.last_login_at
                        ? new Date(targetUser.last_login_at).toLocaleString()
                        : "-"}
                    </td>
                    <td>
                      <ActionIconButton
                        variant="edit"
                        onClick={() => pushPopupButton(targetUser, false)}
                      />
                    </td>
                    <td>
                      {targetUser.id === user?.id ? (
                        <span>-</span>
                      ) : (
                        <ActionIconButton
                          variant="delete"
                          onClick={() => handleDeleteUser(targetUser)}
                        />
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5}>
                    <p className="list-none">ユーザーなし</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {isShowPopup && (
        <UserPopup
          targetUser={targetUser}
          isCSVMode={isCSVMode}
          onClose={() => setIsShowPopup(false)}
          onLoading={onLoading}
          updateUserList={updateUserList}
        />
      )}
    </>
  );
};

export default UsersList;
